import React from 'react';
import { Link } from 'react-router-dom';
import { StaticNavigation, Footer } from '@/modules/landing';

export default function VisionPage() {
  return (
    <div className="min-h-screen bg-zapt-navy relative overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-40"
        style={{ backgroundImage: 'url(https://zapt-cdn.com/dc408fb4-e206-4be6-8677-dfc0f17d99f7/background_image.jpg)' }}
      />
      
      {/* Gradient overlay for text legibility */}
      <div className="absolute inset-0 bg-gradient-to-b from-zapt-navy/60 via-zapt-navy/70 to-zapt-navy/60" />
      
      <StaticNavigation />

      {/* Hero */}
      <div className="pt-24 sm:pt-32 pb-12 sm:pb-16 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-white mb-6 tracking-tight">
            Software That<br />
            <span className="text-zapt-yellow">Grows With You</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed">
            Maybe you've outgrown generic tools that don't fit how you work. Maybe you've never had the right tools at all — just spreadsheets, paper, and messages scattered everywhere.
          </p>
          <p className="text-lg sm:text-xl text-gray-200 mt-6 max-w-2xl mx-auto font-medium">
            Either way, custom software isn't a luxury. It's the path to working the way you actually need to.
          </p>
        </div>
      </div>

      {/* The Ceiling Section */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 relative z-10">
        <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-2xl p-8 sm:p-12 border border-gray-700/50">
          <h2 className="text-2xl sm:text-3xl font-black text-white mb-6">
            The Ceiling
          </h2>
          <p className="text-gray-200 text-lg mb-6">
            Every business hits it eventually.
          </p>
          <p className="text-gray-300 mb-6 leading-relaxed">
            Maybe it's the limits of generic software — tools that work well enough at first but can't adapt to how you've learned to work. Maybe it's the chaos of doing everything manually — bookings in a notebook, client info in your head, communication scattered across texts and emails.
          </p>
          <p className="text-gray-300 mb-8 leading-relaxed">
            Either way, you're stuck. The way you work — the specific thing that makes your business yours — isn't captured anywhere. It's trapped in your head, or squeezed into tools that weren't built for it.
          </p>
          <div className="bg-zapt-purple/20 border border-zapt-purple/30 rounded-xl p-6">
            <p className="text-gray-200 text-lg font-medium">
              When software fits exactly, it compounds over time. Workarounds — whether manual or digital — hold you back.
            </p>
          </div>
        </div>
      </div>

      {/* Where Custom Belongs Section */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 relative z-10">
        <div className="bg-zapt-navy/80 backdrop-blur-sm rounded-2xl p-8 sm:p-12 border border-white/10">
          <h2 className="text-2xl sm:text-3xl font-black text-white mb-6">
            Where Custom Belongs
          </h2>
          <p className="text-gray-300 mb-6 leading-relaxed">
            Not everything needs to be custom. Payments, email, calendars, accounting, contact lists — these are solved problems. Use the best off-the-shelf tools for them. Someone else maintains them, updates them, keeps them secure.
          </p>
          <p className="text-gray-300 mb-8 leading-relaxed">
            But the thing that makes your business <span className="text-white font-medium">yours</span> — the specific workflow, the unique process, the way you actually operate — that's where generic tools fail. They force you to adapt to them, instead of the other way around.
          </p>
          <div className="bg-zapt-yellow/10 border border-zapt-yellow/30 rounded-xl p-6">
            <p className="text-zapt-yellow text-lg font-bold mb-2">
              Custom belongs in the gaps.
            </p>
            <p className="text-gray-200">
              The parts only you understand. The workflow no off-the-shelf tool handles. That's where we come in.
            </p>
          </div>
        </div>
      </div>

      {/* The Cycle Section */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 relative z-10">
        <h2 className="text-2xl sm:text-3xl font-black text-white text-center mb-4">
          The Cycle of Growth
        </h2>
        <p className="text-gray-400 text-center mb-10 max-w-2xl mx-auto">
          Businesses that evolve share a pattern.
        </p>
        
        <div className="space-y-6">
          {/* Start */}
          <div className="bg-zapt-navy/80 backdrop-blur-sm rounded-xl p-6 sm:p-8 border border-white/10">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-zapt-yellow/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <span className="text-zapt-yellow font-black text-lg">01</span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Start</h3>
                <p className="text-gray-300">
                  You need software that actually fits. Maybe you're replacing clunky generic tools. Maybe you're digitising for the first time. Either way, you build something simple that matches your actual workflows.
                </p>
              </div>
            </div>
          </div>

          {/* Grow */}
          <div className="bg-zapt-navy/80 backdrop-blur-sm rounded-xl p-6 sm:p-8 border border-white/10">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-zapt-yellow/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <span className="text-zapt-yellow font-black text-lg">02</span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Grow</h3>
                <p className="text-gray-300">
                  You learn by doing. Your needs sharpen. Your software adapts — new features, new integrations, shaped by what you've discovered.
                </p>
              </div>
            </div>
          </div>

          {/* Lead */}
          <div className="bg-zapt-navy/80 backdrop-blur-sm rounded-xl p-6 sm:p-8 border border-white/10">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-zapt-yellow/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <span className="text-zapt-yellow font-black text-lg">03</span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Lead</h3>
                <p className="text-gray-300 mb-3">
                  Your software now encodes real expertise. It becomes a competitive advantage — a moat that others can't easily replicate.
                </p>
                <p className="text-gray-400 text-sm">
                  Some clients reach a point where their internal tools become products they offer to others. We help them make that transition too.
                </p>
              </div>
            </div>
          </div>
        </div>

        <p className="text-gray-400 text-center mt-8 italic">
          This cycle repeats. Each stage feeds the next.
        </p>
      </div>

      {/* Where ZAPT Fits */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-24 relative z-10">
        <div className="bg-zapt-purple/20 backdrop-blur-sm rounded-2xl p-8 sm:p-12 text-center border border-zapt-purple/30">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-6">
            Where ZAPT Fits
          </h2>
          <p className="text-xl sm:text-2xl text-zapt-yellow font-bold mb-6">
            Custom used to mean expensive. We changed that.
          </p>
          <p className="text-gray-200 text-lg mb-6 max-w-xl mx-auto">
            Not $100k and six months. A few hundred dollars and a few days to start. We help you identify where custom actually belongs, then build exactly that.
          </p>
          <p className="text-gray-300 text-lg mb-8 max-w-xl mx-auto font-medium">
            The build is just the beginning. We're there as you grow — evolving your software as your business evolves.
          </p>
          <Link
            to="/authenticate"
            className="inline-block bg-zapt-yellow text-zapt-navy px-8 py-4 rounded-xl font-bold text-lg hover:bg-zapt-yellow/90 transition-colors"
          >
            Get Started Free →
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
}
